import { useEffect, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { ScalingTextArea } from "@/components/ScalingTextArea/ScalingTextArea";
import { isTouchDevice } from "@/helpers/device";
import { mergeRefs } from "@/helpers/mergeRefs";
import { useTranslation } from "@/translations";

export function CommentFieldInput({
  autoFocus,
  value,
  onChange,
  placeholder,
  isEdit,
  isNote,
  isSubmitting,
  disabled,
  onFocus,
  onBlur,
  ref,
}: {
  autoFocus?: boolean;
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  isEdit?: boolean;
  isNote?: boolean;
  isSubmitting?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  ref?: React.Ref<HTMLTextAreaElement>;
}): React.ReactElement {
  const { t } = useTranslation();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEdit && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [isEdit, textAreaRef]);

  return (
    <ScalingTextArea
      className={twJoin(
        "block min-h-10 w-full resize-none bg-transparent py-2 pl-1 placeholder:text-grey-700 focus:outline-none",
        isSubmitting && "pointer-events-none opacity-30",
        isNote && "bg-yellow-100",
      )}
      disabled={disabled}
      autoFocus={autoFocus && !isTouchDevice()}
      data-testid="comment-field"
      name="content"
      ref={mergeRefs(textAreaRef, ref)}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={
        placeholder ||
        (isNote ? t("component.comment-field.create-new-note") : t("component.comment-field.create-new-reaction"))
      }
      {...{ onFocus, onBlur }}
    />
  );
}
